/**
 * Feuille de brouillon d'UNE question, dans l'éditeur V2 : état, « Générer »,
 * « Tester » (la vraie feuille, ici, sans rien enregistrer), « Publier »,
 * « Désactiver ». Monté sous chaque question (BlockShell).
 *
 * Aucune IA ne tourne quand l'élève joue : l'IA propose le modèle ici, le
 * moteur le valide (porte), et le tuteur publie.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, ChevronDown, FlaskConical, Loader2, PenLine, Sparkles } from 'lucide-react';
import { cn, MarkdownMath, VisualBlocksRenderer } from '@jeraisonne/ui';
import { StoredScratchModelSchema, isEngineCompatible } from '@jeraisonne/reasoner';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import { isAdmin, useAuth } from '@/lib/auth';
import { visualsOf } from '@/lib/exercise-v2/block-helpers';
import { ScratchSheet, ScratchSession } from '@/features/scratch-sheet';
import {
  SHEET_STATE_LABEL,
  generationErrorMessage,
  isEngineReady,
  isValidated,
  rowIssues,
  rowSolution,
  type ScratchRow,
  type SheetState,
} from './scratch-admin';
import {
  useGenerateSheet,
  useIsGenerating,
  useSetSheetStatus,
  useSheetQuestions,
  type GenerateResult,
  type SheetQuestion,
} from './use-scratch-admin';

const PILL: Record<SheetState, string> = {
  none: 'border-ink-200 bg-white text-ink-500',
  ready: 'border-sky-200 bg-sky-50 text-sky-700 ocean:border-sky-400/40 ocean:text-sky-300',
  review: 'border-amber-200 bg-amber-50 text-amber-800 ocean:border-amber-400/40',
  published: 'border-emerald-200 bg-emerald-50 text-emerald-700 ocean:border-emerald-400/40',
  disabled: 'border-ink-200 bg-ink-50 text-ink-500',
  ineligible: 'border-ink-200 bg-ink-50 text-ink-500',
};

export function SheetStatePill({ state }: { state: SheetState }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-sans text-xs font-semibold',
        PILL[state],
      )}
      data-testid="sheet-state"
    >
      {state === 'published' && <CheckCircle2 className="h-3 w-3" aria-hidden />}
      {SHEET_STATE_LABEL[state]}
    </span>
  );
}

const BTN =
  'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 font-sans text-xs font-semibold transition-colors active:scale-[0.98] disabled:opacity-50';

export function outcomeMessage(outcome: GenerateResult): string {
  if (outcome.keptPublished)
    return outcome.kind === 'ineligible'
      ? 'L’IA juge la question hors champ : la feuille publiée reste en ligne (désactive-la si besoin).'
      : 'La nouvelle proposition est refusée par le moteur : la feuille publiée reste en ligne.';
  switch (outcome.kind) {
    case 'ready':
      return 'Feuille prête : teste-la, puis publie-la.';
    case 'review':
      return 'Le moteur refuse encore ce modèle après 3 essais : voir les points à revoir.';
    case 'ineligible':
      return outcome.row.validation.category === 'legacy_engine'
        ? 'Le nouveau moteur ne sait pas encore faire cette question (elle relève de l’ancien) : pas de nouvelle feuille pour l’instant.'
        : 'Question hors du champ du moteur : pas de feuille.';
    case 'not_linked':
      return 'Question non reliée à une SKILLCARD : pas de feuille pour l’instant.';
  }
}

/** Énoncé de la question tel que l'élève le voit (sous-énoncés juste avant + question). */
export function questionLeadIn(blocks: ExerciseBlock[], questionId: string): ExerciseBlock[] {
  const statement = blocks
    .filter((b) => b.section === 'statement')
    .sort((a, b) => a.position - b.position);
  const at = statement.findIndex((b) => b.id === questionId);
  if (at < 0) return [];
  const lead: ExerciseBlock[] = [];
  for (let i = at - 1; i >= 0; i--) {
    const b = statement[i]!;
    if (b.block_type === 'statement_question') break;
    if (b.block_type === 'statement') lead.unshift(b);
  }
  return lead;
}

function BlockText({ block }: { block: ExerciseBlock }) {
  const data = block.data as { content_md?: string; prompt_md?: string; sub_statement_md?: string };
  const visuals = visualsOf(block);
  return (
    <div className="space-y-2">
      {data.sub_statement_md && (
        <p className="text-ink-500 font-sans text-xs">
          <MarkdownMath>{data.sub_statement_md}</MarkdownMath>
        </p>
      )}
      <div
        className={cn(
          'font-sans leading-relaxed',
          block.block_type === 'statement_question'
            ? 'text-ink-900 text-base'
            : 'text-ink-700 text-sm',
        )}
      >
        <MarkdownMath>{data.prompt_md ?? data.content_md ?? ''}</MarkdownMath>
      </div>
      {visuals.length > 0 && <VisualBlocksRenderer blocks={visuals} />}
    </div>
  );
}

/** La feuille de la question, jouée ici même : rien n'est enregistré. */
export function SheetTester({
  row,
  statement,
  onClose,
}: {
  row: ScratchRow;
  statement: ReactNode;
  onClose: () => void;
}) {
  const session = useMemo(() => {
    const parsed = StoredScratchModelSchema.safeParse({
      engine_version: row.engine_version,
      model: row.model,
    });
    if (!parsed.success || !isEngineCompatible(parsed.data.engine_version)) return null;
    try {
      return new ScratchSession({ model: parsed.data.model, maxHora: row.max_hora });
    } catch {
      return null;
    }
  }, [row.engine_version, row.model, row.max_hora]);
  useEffect(() => () => session?.dispose(), [session]);
  if (!session) {
    return (
      <div
        role="alertdialog"
        aria-label="Feuille illisible"
        className="ocean:border-amber-400/40 fixed inset-x-4 bottom-4 z-50 rounded-2xl border border-amber-200 bg-amber-50 p-4 font-sans text-sm text-amber-800 shadow-lg sm:inset-x-auto sm:right-6 sm:w-96"
      >
        <p>Ce modèle ne se charge pas dans le moteur : régénère la feuille.</p>
        <button
          type="button"
          onClick={onClose}
          autoFocus
          className={cn(BTN, 'mt-3 border-amber-300 bg-white')}
        >
          Fermer
        </button>
      </div>
    );
  }
  return (
    <ScratchSheet
      session={session}
      draft={row.status !== 'published'}
      onClose={onClose}
      statement={statement}
    />
  );
}

export interface QuestionScratchToolsProps {
  exerciseId: string;
  question: ExerciseBlock;
  allBlocks: ExerciseBlock[];
}

export function QuestionScratchTools({
  exerciseId,
  question,
  allBlocks,
}: QuestionScratchToolsProps) {
  const sheets = useSheetQuestions(exerciseId, allBlocks);
  const q = sheets.questions.find((x) => x.id === question.id);
  // Exercice sans aucune question reliée : le panneau l'explique, rien sous chaque question.
  if (!q || !sheets.anyLinked) return null;
  return <QuestionScratchToolsView exerciseId={exerciseId} q={q} allBlocks={allBlocks} />;
}

/** Pastille toujours visible sur la question (mode lecture de l'éditeur). */
export function QuestionSheetBadge({ exerciseId, question, allBlocks }: QuestionScratchToolsProps) {
  const sheets = useSheetQuestions(exerciseId, allBlocks);
  const q = sheets.questions.find((x) => x.id === question.id);
  if (!q || !sheets.anyLinked || !q.linked) return null;
  // seule la feuille de l'ancien moteur est en ligne
  const legacyOnly = q.shown === 'none' && q.legacyState === 'published';
  return (
    <span className="inline-flex items-center gap-1.5" title="Feuille de brouillon">
      <PenLine className="text-ink-400 h-3 w-3" aria-hidden />
      <span className="sr-only">Feuille de brouillon :</span>
      <SheetStatePill state={legacyOnly ? 'published' : q.shown} />
      {legacyOnly && <span className="text-ink-400 font-sans text-xs">ancien moteur</span>}
    </span>
  );
}

export function QuestionScratchToolsView({
  exerciseId,
  q,
  allBlocks,
}: {
  exerciseId: string;
  q: SheetQuestion;
  allBlocks: ExerciseBlock[];
}) {
  const generate = useGenerateSheet(exerciseId);
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const [opusRun, setOpusRun] = useState(false);
  const setStatus = useSetSheetStatus(exerciseId);
  const generating = useIsGenerating(exerciseId);
  const [attempt, setAttempt] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [instructions, setInstructions] = useState('');
  const [testing, setTesting] = useState(false);
  const [showSolution, setShowSolution] = useState(false);

  if (!q.linked) {
    return (
      <p className="text-ink-400 mt-2 border-t border-slate-100 pt-2 font-sans text-xs">
        Feuille de brouillon : question non reliée à une SKILLCARD.
      </p>
    );
  }

  // une génération à la fois par exercice (ici ou dans le panneau)
  const busy = generating || setStatus.isPending;
  const row = q.row;
  const issues = q.stale
    ? [
        {
          code: 'stale',
          message: q.blocker
            ? `${q.blocker} La feuille existante ne correspond plus.`
            : 'Le QCM a changé depuis la génération (choix ajoutés, retirés ou remplacés) : régénère la feuille.',
        },
      ]
    : rowIssues(row);
  const solution = rowSolution(row);
  const playable = !!row && row.status !== 'ineligible' && isValidated(row) && isEngineReady(row);
  const canPublish = playable && !q.stale && (q.state === 'ready' || q.state === 'disabled');
  const canTest = playable && !q.stale;
  // QCM sans réponse ou à plusieurs bonnes réponses : rien à générer
  const canGenerate = !q.blocker;

  async function onGenerate(forceOpus = false) {
    if (
      q.state === 'published' &&
      !window.confirm(
        'Cette feuille est publiée. Si la nouvelle proposition passe le moteur, elle la remplacera en brouillon, à republier. Continuer ?',
      )
    )
      return;
    setMessage(null);
    setAttempt(0);
    setOpusRun(forceOpus);
    try {
      const outcome = await generate.mutateAsync({
        question: q,
        customInstructions: instructions,
        onAttempt: setAttempt,
        ...(forceOpus ? { forceOpus: true } : {}),
      });
      setMessage(outcomeMessage(outcome));
    } catch (e) {
      setMessage(generationErrorMessage(e));
    }
  }

  async function changeStatus(status: 'published' | 'disabled', target: ScratchRow | null = row) {
    if (!target) return;
    setMessage(null);
    try {
      await setStatus.mutateAsync({ ids: [target.id], status });
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    }
  }

  const lead = questionLeadIn(allBlocks, q.id);

  return (
    <div
      className="mt-2 space-y-2 border-t border-slate-100 pt-2"
      data-testid="question-scratch-tools"
    >
      <div className="flex flex-wrap items-center gap-2">
        <PenLine className="text-ink-500 h-3.5 w-3.5" aria-hidden />
        <span className="text-ink-700 font-sans text-xs font-semibold">Feuille de brouillon</span>
        <SheetStatePill state={q.shown} />
        {row && q.state !== 'ineligible' && row.max_hora > 0 && (
          <span className="text-ink-400 font-sans text-xs">jusqu’à {row.max_hora} HORA</span>
        )}
      </div>

      {q.legacy && (q.legacyState === 'published' || q.legacyState === 'disabled') && (
        <div className="flex flex-wrap items-center gap-2" data-testid="legacy-sheet">
          <span className="text-ink-500 font-sans text-xs">Ancien moteur :</span>
          <SheetStatePill state={q.legacyState} />
          <span className="text-ink-400 font-sans text-xs">
            {q.row?.status === 'published'
              ? 'jouée seulement par les apps pas encore à jour'
              : 'jouée tant qu’il n’y a pas de version nouveau moteur publiée'}
          </span>
          <button
            type="button"
            onClick={() =>
              void changeStatus(q.legacyState === 'published' ? 'disabled' : 'published', q.legacy)
            }
            disabled={busy}
            className={cn(BTN, 'border-ink-200 text-ink-700 bg-white')}
          >
            {q.legacyState === 'published' ? 'Désactiver l’ancienne' : 'Réactiver l’ancienne'}
          </button>
        </div>
      )}

      {q.blocker && !row && <p className="text-ink-500 font-sans text-xs">{q.blocker}</p>}
      {q.state === 'ineligible' && row?.ineligible_reason && (
        <p className="text-ink-500 font-sans text-xs">{row.ineligible_reason}</p>
      )}
      {q.shown === 'review' && issues.length > 0 && (
        <ul className="ocean:border-amber-400/40 space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 font-sans text-xs text-amber-800">
          {issues.slice(0, 5).map((i, k) => (
            <li key={k}>
              {i.message}
              {i.line ? <span className="font-mono"> — {i.line}</span> : null}
            </li>
          ))}
          {issues.length > 5 && <li>… et {issues.length - 5} autre(s).</li>}
        </ul>
      )}
      {solution.length > 0 && q.shown !== 'review' && (
        <div>
          <button
            type="button"
            onClick={() => setShowSolution((s) => !s)}
            aria-expanded={showSolution}
            className="text-ink-500 hover:text-ink-900 inline-flex min-h-11 items-center gap-1 font-sans text-xs"
          >
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', showSolution && 'rotate-180')}
              aria-hidden
            />
            Corrigé trouvé par le moteur ({solution.length} lignes)
          </button>
          {showSolution && (
            <ol className="text-ink-700 ml-5 list-decimal space-y-0.5 font-sans text-xs">
              {solution.map((s, k) => (
                <li key={k}>
                  <span className="font-mono">{s.text}</span>
                  {s.label && <span className="text-ink-400"> · {s.label}</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {canGenerate && (
        <details className="group">
          <summary className="text-ink-500 hover:text-ink-900 inline-flex min-h-11 cursor-pointer list-none items-center gap-1 font-sans text-xs">
            <ChevronDown
              className="h-3.5 w-3.5 transition-transform group-open:rotate-180"
              aria-hidden
            />
            Consigne pour l’IA (facultatif)
          </summary>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value.slice(0, 2000))}
            rows={2}
            aria-label="Consigne pour l’IA"
            placeholder="Ex. : la feuille doit passer par l’aire de la base avant le volume."
            className="border-ink-200 mt-1 w-full rounded-lg border bg-white px-3 py-2 font-sans text-sm"
          />
        </details>
      )}

      {(canGenerate || canTest || canPublish || row?.status === 'published') && (
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          {canGenerate && (
            <button
              type="button"
              onClick={() => void onGenerate()}
              disabled={busy}
              className={cn(
                BTN,
                'ocean:border-violet-400/40 ocean:text-violet-300 border-violet-200 bg-violet-50 text-violet-800',
              )}
            >
              {generate.isPending && !opusRun ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
              )}
              {generate.isPending && !opusRun
                ? attempt > 1
                  ? `Relance ${attempt}/3…`
                  : 'Proposition…'
                : row
                  ? 'Régénérer'
                  : 'Générer'}
            </button>
          )}
          {canGenerate && admin && (
            <button
              type="button"
              onClick={() => void onGenerate(true)}
              disabled={busy}
              title="Admin : tous les essais avec le modèle le plus fort (Opus), plus lent et plus cher"
              className={cn(
                BTN,
                'ocean:border-fuchsia-400/40 ocean:text-fuchsia-300 border-fuchsia-200 bg-white text-fuchsia-800',
              )}
            >
              {generate.isPending && opusRun ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
              )}
              {generate.isPending && opusRun
                ? attempt > 1
                  ? `Opus ${attempt}/3…`
                  : 'Opus…'
                : row
                  ? 'Régénérer avec Opus'
                  : 'Générer avec Opus'}
            </button>
          )}
          {canTest && (
            <button
              type="button"
              onClick={() => setTesting(true)}
              disabled={busy}
              className={cn(
                BTN,
                'ocean:border-sky-400/40 ocean:text-sky-300 border-sky-200 bg-white text-sky-800',
              )}
            >
              <FlaskConical className="h-3.5 w-3.5" aria-hidden />
              Tester
            </button>
          )}
          {canPublish && (
            <button
              type="button"
              onClick={() => void changeStatus('published')}
              disabled={busy}
              className={cn(
                BTN,
                'ocean:text-[#052e1f] border-emerald-300 bg-emerald-600 text-white',
              )}
            >
              Publier
            </button>
          )}
          {row?.status === 'published' && (
            <button
              type="button"
              onClick={() => void changeStatus('disabled')}
              disabled={busy}
              className={cn(BTN, 'border-ink-200 text-ink-700 bg-white')}
            >
              Désactiver
            </button>
          )}
        </div>
      )}

      {message && (
        <p role="status" className="text-ink-600 font-sans text-xs">
          {message}
        </p>
      )}

      {testing && row && (
        <SheetTester
          row={row}
          onClose={() => setTesting(false)}
          statement={
            <div className="space-y-2">
              {lead.map((b) => (
                <BlockText key={b.id} block={b} />
              ))}
              <BlockText block={q.block} />
            </div>
          }
        />
      )}
    </div>
  );
}
